/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import mbpLogger from 'mbp-logger';
import { useSelector } from 'react-redux';

import { string } from 'prop-types';
import useSalesforceProductRecommendationQuery from '../../../../helpers/Personalization/useSalesforceProductRecommendationQuery';
import useClickStreamCustomEventsTracking from '../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import CarouselBuilderSkeleton from '../../../../components/GraphqlComponents/GraphqlSkeletonComponents/CarouselBuilderSkeleton';
import ComponentErrorBoundary from '../../../../components/GraphqlComponents/EnterpriseID/Common/ComponentErrorBoundary/ComponentErrorBoundary';
import PersonalizedCarouselBuilder from '../../../../components/GraphqlComponents/EnterpriseID/Common/PersonalizedCarouselBuilder';
import { getFeatureFlag } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const GiftListRecommendationsSFContainer = ({ title_font_size, title_font_style, title_font_family }) => {
    const isSFGiftListRecsEnabled =  useSelector(getFeatureFlag('is-sf-giftlist-recs-enabled'));
    if (!isSFGiftListRecsEnabled) { return null; }

    const campaignFeature = 'Giftlist Guest';
    const {
        loading, error, data, variables,
    } = useSalesforceProductRecommendationQuery({
        queryName: 'GiftListProductRecommendations',
        campaignFeature,
        targeting: {
            pageType: 'product',
        },
        filterByZipCode: true,
        optInHoldoutParticipation: true,
        resolveIdentity: true,
    });

    const trackClickThrough = useClickStreamCustomEventsTracking({
        salesforceResponse: data?.productRecommendationsSFMulti?.[0],
        isFireImpression: true,
        page: { type: 'product' },
    });

    if (loading || variables?.skip) {
        return (
            <CarouselBuilderSkeleton
                nDisplayProducts={5}
                breakPoints={{ mobile: 601, desktop: 769 }}
            />
        );
    }

    if (error) {
        mbpLogger.logError({
            function: 'GiftListRecommendationsSFContainer.js',
            message: 'Error loading data from saleforce api',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: error,
        });
    }

    if (data?.productRecommendationsSFMulti?.[0]) {
        const title = data?.productRecommendationsSFMulti?.[0]?.campaign?.campaignResponses?.[0]?.payload?.productRecTitle;
        return (
            <ComponentErrorBoundary function="GiftListRecommendationsSFContainer" module="PersonalizedContent">
                <PersonalizedCarouselBuilder
                    blockData={
                        {
                            title: title || 'Recommended For You',
                            title_font_size,
                            title_font_style,
                            title_font_family,
                            wcs_style: false,
                        }
                    }
                    productClickCallback={trackClickThrough}
                    products={data.productRecommendationsSFMulti?.[0]?.products || []}
                    nDisplayProducts={8}
                    demandType="XS"
                    carouselUIComponent="giftListRecommendationsCarousel"
                />
            </ComponentErrorBoundary>
        );
    }

    return null;
};

GiftListRecommendationsSFContainer.propTypes = {
    title_font_size: string,
    title_font_style: string,
    title_font_family: string,
};

GiftListRecommendationsSFContainer.defaultProps = {
    title_font_size: '',
    title_font_style: '',
    title_font_family: '',
};

export default GiftListRecommendationsSFContainer;
